'use client';

import { Loader2, Send } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { useClawFolder } from '../../_hook/use-claw-folder';
import type { ClawFileItem, StoryPayload } from '../../types';
import { StoryShotPane } from './StoryShotPane';
import { useStoryDraft } from './use-story-draft';

interface StoryDetailPanelProps {
  storyPayload: StoryPayload | null;
  /** 默认目录（通常是 session 绑定的 media 子目录），仅在右侧 media 未打开时使用 */
  defaultFolderPath: string;
  /** 父级正在提交中（UpdateMessage / SubmitMessageTasks 进行中），用于在按钮上展示 loading */
  submitting?: boolean;
  onSubmit: (
    payload: StoryPayload,
  ) =>
    | Promise<{ ok: boolean; error?: string; suppressToast?: boolean }>
    | { ok: boolean; error?: string; suppressToast?: boolean };
  onClose?: () => void;
  /** 只读模式：分享会话等场景下隐藏提交、aspectRatio 选择器与编辑能力 */
  readOnly?: boolean;
  /** 只读模式时强制使用的 clawId，覆盖 store 中的 activeClaw */
  overrideClawId?: string;
  /** 打开时是否自动收起左侧 session 侧栏。Chat 主页希望腾出横向空间，
   *  Dialog 形态（如分享页）下传 false 避免污染父级 nav 状态。默认 true。 */
  autoCollapseSidebar?: boolean;
  className?: string;
}

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'];

export const StoryDetailPanel = ({
  storyPayload,
  defaultFolderPath,
  submitting: externalSubmitting,
  onSubmit,
  onClose,
  readOnly = false,
  overrideClawId,
  autoCollapseSidebar = true,
  className,
}: StoryDetailPanelProps) => {
  const t = useTranslations('claw.storyDetail.panel');
  const {
    activeClawId: storeActiveClawId,
    mediaFolderPath,
    mediaPanelOpen,
    mediaRefreshKey,
    toggleClawSidebar,
  } = useStore((state) => ({
    activeClawId: state.activeClaw?.id,
    mediaFolderPath: state.clawFolderPath,
    mediaPanelOpen: state.clawRightPanelOpen,
    mediaRefreshKey: state.clawFileListRefreshKey,
    toggleClawSidebar: state.toggleClawSidebar,
  }));
  const activeClawId = overrideClawId ?? storeActiveClawId;
  const draftApi = useStoryDraft(storyPayload);
  const [internalSubmitting, setSubmitting] = useState(false);
  // 任一来源在 submitting 都视为提交中：内部 onSubmit 调用期间 + 父组件透传的全局 submitting
  const submitting = internalSubmitting || !!externalSubmitting;
  const [files, setFiles] = useState<ClawFileItem[]>([]);
  const { listFolder } = useClawFolder(activeClawId);

  // 分镜详情打开时自动收起左侧 session 侧栏，让 media + 分镜编辑区域获得完整横向空间。
  // Dialog 形态（分享页）通过 autoCollapseSidebar=false 关闭该副作用，避免污染父级 nav。
  useEffect(() => {
    if (storyPayload && autoCollapseSidebar) toggleClawSidebar(true);
  }, [storyPayload, autoCollapseSidebar, toggleClawSidebar]);

  // media 面板已打开时，候选 = media 当前目录；面板未打开时退回 session 默认目录。
  // refreshKey 跟着 media 列表的刷新一起 bump，能在 media 改动后同步刷新候选。
  const effectiveFolderPath = mediaPanelOpen && mediaFolderPath ? mediaFolderPath : defaultFolderPath;

  useEffect(() => {
    if (!storyPayload || !effectiveFolderPath || !activeClawId) {
      setFiles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await listFolder({ folderPath: effectiveFolderPath });
      if (cancelled) return;
      if (data) setFiles(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [storyPayload, effectiveFolderPath, activeClawId, listFolder, mediaRefreshKey]);

  const handleSubmit = useCallback(async () => {
    if (!draftApi || submitting) return;
    setSubmitting(true);
    const result = await onSubmit(draftApi.draft);
    setSubmitting(false);
    if (result.ok) {
      if (!result.suppressToast) {
        toast({ type: 'success', message: t('submitSuccess') });
      }
      draftApi.resetDirty();
      onClose?.();
    } else if (!result.suppressToast) {
      toast({ type: 'error', message: result.error ?? t('submitFailed') });
    }
  }, [draftApi, submitting, onSubmit, onClose, t]);

  if (!storyPayload) return null;

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col border-system-white08 border-x bg-[#0A0D10] light:bg-system-bg01',
        className,
      )}
    >
      <header className="flex shrink-0 flex-col gap-3 border-system-white08 border-b px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-sm text-system-white">{t('title')}</div>
            <div className="truncate text-system-white48 text-xs">
              {storyPayload.mdFileName ?? 'story.md'} · {t('shotCount', { count: storyPayload.shots.length })}
            </div>
          </div>
          {readOnly ? null : (
            <Button
              type="button"
              size="sm"
              disabled={submitting || !draftApi}
              className="bg-[#5765F2] text-white hover:bg-[#4955D8] disabled:bg-system-white12 disabled:text-system-white36"
              onClick={handleSubmit}
            >
              {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Send className="mr-2 size-4" />}
              {submitting ? t('submitting') : t('submit')}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="shrink-0 text-system-white48 text-xs">{t('aspectRatio')}</span>
          {readOnly ? (
            <span className="rounded-8 bg-system-white08 px-2 py-1 text-system-white80 text-xs">
              {storyPayload.globalConfig.aspectRatio}
            </span>
          ) : (
            <Select
              value={draftApi?.draft.globalConfig.aspectRatio ?? storyPayload.globalConfig.aspectRatio}
              onValueChange={(value) => draftApi?.updateGlobalConfig({ aspectRatio: value })}
            >
              <SelectTrigger className="h-8 w-24 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASPECT_RATIO_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 p-3">
        {draftApi ? (
          <StoryShotPane
            shots={draftApi.draft.shots}
            activeShotIndex={draftApi.activeShotIndex}
            activeShot={draftApi.activeShot}
            files={files}
            onPrev={draftApi.goPrevShot}
            onNext={draftApi.goNextShot}
            onSelectShot={draftApi.setActiveShotIndex}
            onUpdateShot={draftApi.updateShot}
            onAddAsset={draftApi.addAssetToShot}
            onRemoveAsset={draftApi.removeAssetFromShot}
            readOnly={readOnly}
            className="rounded-12 border border-system-white08"
          />
        ) : (
          <div className="flex h-full items-center justify-center rounded-12 border border-system-white08 text-sm text-system-white48">
            {t('initializing')}
          </div>
        )}
      </div>
    </div>
  );
};
